import { loginRequestSchema, signInClientSchema, type SignInClient } from '@instanexus/contracts';
import { z } from 'zod';

/** One key; a second Continue with Google in the same tab overwrites the first, which is what the person meant. */
export const FEDERATED_RETURN_KEY = 'inx_federated_return';

interface RoundTripMemory {
  /** The provider login hint the round trip was started with (lower-case email), so a restart can reuse it. */
  readonly loginHint?: string;
  /** Set on the entry saved for a restart, so a second restart in a row is shown as a refusal. */
  readonly restarted?: true;
}

export type FederatedReturn =
  /** The `/login` query string WITHOUT the leading '?', e.g. 'next=%2Flocations&client=admin&code_challenge=…&state=…'. */
  | ({ readonly kind: 'sign_in'; readonly search: string } & RoundTripMemory)
  /** An invitation accepted with a provider: the person lands on that app's origin afterwards. */
  | ({ readonly kind: 'invite'; readonly client: SignInClient } & RoundTripMemory)
  /** A sign-up started with a provider: the person lands on the organization step afterwards. */
  | ({ readonly kind: 'signup' } & RoundTripMemory)
  /** An admin's company test sign-in (S50-kqp9 Phase 4a): the person lands back on that app's Settings → Company sign-in. */
  | ({ readonly kind: 'company_test'; readonly client: SignInClient } & RoundTripMemory);

const MAX_SEARCH_LENGTH = 2048;

const memory = {
  loginHint: loginRequestSchema.shape.email.optional(),
  restarted: z.literal(true).optional(),
};

const federatedReturnSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('sign_in'),
    search: z
      .string()
      .max(MAX_SEARCH_LENGTH)
      .refine((search) => !search.startsWith('?')),
    ...memory,
  }),
  z.object({ kind: z.literal('invite'), client: signInClientSchema, ...memory }),
  z.object({ kind: z.literal('signup'), ...memory }),
  z.object({ kind: z.literal('company_test'), client: signInClientSchema, ...memory }),
]);

/** JSON into sessionStorage (tab-scoped, survives the provider round trip on the same origin). */
export function saveFederatedReturn(
  value: FederatedReturn,
  storage: Storage = window.sessionStorage,
): void {
  storage.setItem(FEDERATED_RETURN_KEY, JSON.stringify(value));
}

/** Reads AND removes the entry (single use); anything missing or invalid is null. */
export function takeFederatedReturn(storage?: Storage): FederatedReturn | null {
  try {
    const store = storage ?? window.sessionStorage;
    const raw = store.getItem(FEDERATED_RETURN_KEY);
    if (raw === null) return null;
    store.removeItem(FEDERATED_RETURN_KEY);
    const parsed = federatedReturnSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** The refusal code the federated callback page passes in router state, or null. */
export function readFederatedErrorCode(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('federatedErrorCode' in state)) return null;
  const { federatedErrorCode } = state as { federatedErrorCode: unknown };
  return typeof federatedErrorCode === 'string' ? federatedErrorCode : null;
}
